import { useCallback, useRef } from 'react';

import { useAuth } from '@/utils/auth/context';
import { envVariables } from '@/utils/env';

import { useFetchWithAuth } from '../useFetchWithAuth';

import { getPendingLogs, PendingLogEntry, queuePendingLog, setPendingLogs } from './pendingLogQueue';

/**
 * Deze 'useFrontendLogger'-hook biedt diverse callbacks aan voor het doorgeven van een event (e.g. succesvolle login, refresh failed)
 * aan de backend, zodat dit gelogd kan worden etc., omdat dit een side-effect is (imperatief) en geen raakvlakken heeft met de
 * React Query state of cache gebruiken we hiervoor niet de useQuery hook maar een losse fetch (POST)
 */

const frontendLoggerRequestInit: RequestInit = {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
};

export const useFrontendLogger = () => {
  const { fetchWithAuth } = useFetchWithAuth();
  const auth = useAuth();
  const logDumpEnabled = envVariables().authProvider !== 'bff-cookie';

  const isFlushingRef = useRef(false);

  // Probeer een niet eerder verstuurde logmelding (bv. opgeslagen omdat de client offline was) alsnog te
  // versturen. Meldingen die opnieuw falen blijven in de wachtrij staan voor een volgende poging.
  const flushPendingLogs = useCallback(async () => {
    if (isFlushingRef.current) return;

    const entries = getPendingLogs();
    if (entries.length === 0) return;

    isFlushingRef.current = true;
    const processedIds = new Set(entries.map((entry) => entry.id));
    try {
      const stillFailing: PendingLogEntry[] = [];
      for (const entry of entries) {
        // Azure ondersteunt /log-dump niet; verwijder ook eerder opgeslagen dumps uit de wachtrij.
        if (!logDumpEnabled && entry.endpoint === '/log-dump') continue;

        try {
          const request = { ...frontendLoggerRequestInit, body: JSON.stringify(entry.body) };
          const response = await fetchWithAuth(entry.endpoint, request);
          if (!response.ok) {
            throw new Error(`Loggen naar ${entry.endpoint} mislukt met status ${response.status}`);
          }
        } catch {
          stillFailing.push(entry);
        }
      }
      const queuedDuringFlush = getPendingLogs().filter((entry) => !processedIds.has(entry.id));
      setPendingLogs([...stillFailing, ...queuedDuringFlush]);
    } finally {
      isFlushingRef.current = false;
    }
  }, [fetchWithAuth, logDumpEnabled]);

  return {
    loginSuccessful: useCallback(() => {
      fetchWithAuth('/login-succesvol', frontendLoggerRequestInit).catch((error) => {
        console.warn('Loggen naar /login-succesvol mislukt', error);
      });
    }, [fetchWithAuth, auth]),

    refreshFailed: useCallback(
      async (refreshFoutmelding: string) => {
        const body = { refreshFoutmelding };
        const request = { ...frontendLoggerRequestInit, body: JSON.stringify(body) };
        try {
          const response = await fetchWithAuth('/log-token-refresh-fout', request);
          if (!response.ok) {
            throw new Error(`Loggen naar /log-token-refresh-fout mislukt met status ${response.status}`);
          }
        } catch {
          queuePendingLog('/log-token-refresh-fout', body);
        }
      },
      [fetchWithAuth],
    ),

    logDump: useCallback(
      async (logDump: string) => {
        if (!logDumpEnabled) return;

        const body = { logDump };
        const request = { ...frontendLoggerRequestInit, body: JSON.stringify(body) };
        try {
          const response = await fetchWithAuth('/log-dump', request);
          if (!response.ok) {
            throw new Error(`Loggen naar /log-dump mislukt met status ${response.status}`);
          }
        } catch {
          queuePendingLog('/log-dump', body);
        }
      },
      [fetchWithAuth, logDumpEnabled],
    ),

    flushPendingLogs,
  };
};
